import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppDispatch,
  RootState,
  getChannelId,
  passkeySignInRequest,
  resetPasskeyError,
  useSecureDeviceId,
} from '@rb-serve-app/core';
import {
  authenticateWithPasskey,
  initializeTransmitSdk,
  isWebAuthnSupported,
} from '../../services/transmitWebAuthn';
import { isPasskeyTerminalMwError } from './evaluateAuthMethod';
import {
  PasskeyWebSignInError,
  classifyPasskeyWebSignInError,
  passkeyMiddlewareError,
} from './passkeyWebSignInErrors';

export type PasskeyAttemptResult =
  | { kind: 'blocked' }
  | { kind: 'cancelled' }
  | { kind: 'unavailable'; error: PasskeyWebSignInError }
  | { kind: 'failed'; error: PasskeyWebSignInError }
  | { kind: 'check_failed'; error?: PasskeyWebSignInError }
  | { kind: 'submitted' };

interface UsePasskeySignInParams {
  isPasswordLoading: boolean;
  /** When false, do not warm up or call the Transmit SDK. */
  allowPasskeySdk?: boolean;
  /** When true, pass the username into the Transmit WebAuthn modal. */
  passUsernameToTransmit?: boolean;
}

interface UsePasskeySignInResult {
  /** True when the browser supports WebAuthn. */
  isPasskeySupported: boolean;
  /** True while the native passkey dialog is open / auth is in flight. */
  isPasskeyLoading: boolean;
  /** Passkey-only SDK/MW error; null when there is none. */
  passkeyError: PasskeyWebSignInError | null;
  passkeyStatusCode: string;
  clearPasskeyError: () => void;
  handlePasskeySignIn: (username: string) => Promise<PasskeyAttemptResult>;
}

/**
 * Passkey sign-in flow (Transmit Security). Kept independent of the password
 * form: it does not touch form state, recaptcha, or signInRequest.
 *
 * Two phases:
 * 1. Transmit SDK native modal → base64 encoded WebAuthn assertion
 *    (local loading/error state)
 * 2. Middleware authenticate call via Redux saga
 *    (passkeyLoading / passkeyError from the user slice)
 */
export const usePasskeySignIn = ({
  isPasswordLoading,
  allowPasskeySdk = true,
  passUsernameToTransmit = false,
}: UsePasskeySignInParams): UsePasskeySignInResult => {
  const dispatch = useDispatch<AppDispatch>();
  const deviceId = useSecureDeviceId() || '';
  const [isSdkLoading, setIsSdkLoading] = useState(false);
  const [sdkError, setSdkError] = useState<PasskeyWebSignInError | null>(null);
  const isMountedRef = useRef(true);

  const {
    passkeyLoading,
    passkeyError: mwError,
    passkeyStatusCode,
  } = useSelector((state: RootState) => state.user);

  const isPasskeySupported = isWebAuthnSupported();
  const isPasskeyLoading = isSdkLoading || !!passkeyLoading;

  useEffect(() => {
    isMountedRef.current = true;
    // Warm up the SDK on mount so authenticate.modal() runs inside the
    // user-activation window of the button click. Failures here are not
    // surfaced; authenticateWithPasskey() will retry initialization.
    if (allowPasskeySdk && isPasskeySupported) {
      initializeTransmitSdk().catch((error: unknown) => {
        console.warn('[Passkey] Transmit SDK initialization failed:', error);
      });
    }
    return () => {
      isMountedRef.current = false;
      // Don't leave a stale passkey error behind when leaving the screen.
      dispatch(resetPasskeyError());
    };
  }, [allowPasskeySdk, isPasskeySupported, dispatch]);

  const clearPasskeyError = useCallback(() => {
    setSdkError(null);
    dispatch(resetPasskeyError());
  }, [dispatch]);

  const handlePasskeySignIn = useCallback(async (
    username: string
  ): Promise<PasskeyAttemptResult> => {
    if (!allowPasskeySdk || isPasskeyLoading || isPasswordLoading) {
      return { kind: 'blocked' };
    }

    setSdkError(null);
    dispatch(resetPasskeyError());
    setIsSdkLoading(true);

    const normalizedUsername = String(username ?? '').trim().toLowerCase();

    try {
      const encodedResult = await authenticateWithPasskey(
        normalizedUsername,
        passUsernameToTransmit
      );

      if (!isMountedRef.current) {
        return { kind: 'blocked' };
      }

      if (!encodedResult) {
        const error: PasskeyWebSignInError = {
          errCode: 'EMPTY_RESULT',
          msg: 'Passkey sign-in did not complete. Please try again or use your password.',
        };
        setSdkError(error);
        return { kind: 'failed', error };
      }

      // Middleware completes the flow against Transmit and issues our own
      // session; tokens are saved from the response body by axiosSetup.
      dispatch(
        passkeySignInRequest({
          apiName: 'passkey_sign_in',
          data: {
            webauthn_encoded_result: encodedResult,
            flow: 'PASSKEY_AUTHENTICATE_API_FLOW',
            deviceId,
            channel: getChannelId,
            username: normalizedUsername,
          },
        })
      );
      return { kind: 'submitted' };
    } catch (error) {
      if (!isMountedRef.current) {
        return { kind: 'blocked' };
      }

      const classified = classifyPasskeyWebSignInError(error);
      if (classified.kind === 'cancelled') {
        console.log('[Passkey] Authentication cancelled by user');
        return { kind: 'cancelled' };
      }
      if (classified.kind === 'unavailable') {
        return {
          kind: 'unavailable',
          error: classified.error as PasskeyWebSignInError,
        };
      }
      if (classified.kind === 'check_failed') {
        return { kind: 'check_failed', error: classified.error ?? undefined };
      }
      const failedError = classified.error as PasskeyWebSignInError;
      setSdkError(failedError);
      return { kind: 'failed', error: failedError };
    } finally {
      if (isMountedRef.current) {
        setIsSdkLoading(false);
      }
    }
  }, [
    allowPasskeySdk,
    dispatch,
    deviceId,
    isPasskeyLoading,
    isPasswordLoading,
    passUsernameToTransmit,
  ]);

  // Middleware errors surface as a passkey-only banner, except corporate /
  // terminal failures which SignIn shows with the existing modals.
  const isCorporatePasskeyError = mwError?.errCode === 'COORP_401_USER_ERROR';
  const passkeyError =
    sdkError ??
    (mwError &&
    !isCorporatePasskeyError &&
    !isPasskeyTerminalMwError(mwError)
      ? passkeyMiddlewareError()
      : null);

  return {
    isPasskeySupported,
    isPasskeyLoading,
    passkeyError,
    passkeyStatusCode: passkeyStatusCode || '',
    clearPasskeyError,
    handlePasskeySignIn,
  };
};
